// src/pages/TopPage.tsx - PixLandingPage構成ベース
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { PhotoIcon, XMarkIcon, HomeIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { config } from '../config';
import { useAuth } from '../contexts/AuthContext';
import CardViewerModal from '../components/gallery/CardViewerModal';
import CharacterCreateWizard from '../components/character/CharacterCreateWizard';
import LoginModal from '../components/auth/LoginModal';
import { CHARACTER_TAGS } from '../constants/characterTags';
import { getFollowedCharacters } from '../services/characterService';
import { listCharactersForSale, CharacterForSale, formatPrice } from '../services/adoptionService';
import { useTranslation } from 'react-i18next';
import { useThemeStore } from '../stores/themeStore';
import kdvrLogo from '../assets/images/UI_FIGRYNE_KDVR_LOGO.png';
import kdvrLogoWhite from '../assets/images/UI_FIGRYNE_KDVR_LOGO_WHITE.png';

// Constants
const PAGE_SIZE = 6;
const CACHE_EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24時間

// Category type filter (オリキャラ or Vtuber)
type CategoryFilter = 'original' | 'vtuber';

// Category preference cache key
const CATEGORY_CACHE_KEY = 'orifoli_category';

// Character item interface
interface CharacterItem {
  id: string;
  name: string;
  nameReading?: string;
  nameEnglish?: string;
  category?: 'original' | 'vtuber';
  bio?: string;
  // XOR-Pix encrypted image fields (CDN primary)
  noise1Url?: string;
  noise2Url?: string;
  rid?: string;
  imageWidth?: number;
  imageHeight?: number;
  thumbnailUrl?: string;
  // XOR-Pix fallback URLs (signed GCS URLs)
  fallbackNoise1Url?: string;
  fallbackNoise2Url?: string;
  // Legacy field
  avatarUrl?: string;
  headerImageUrl?: string;
  species?: string;
  tags?: string[];
  cardTheme?: 'dark' | 'light';
  ownerId: string;
  ownerName?: string;
  ownerUsername?: string;
  ownerAvatar?: string;
  createdAt?: string;
  stats?: {
    followerCount: number;
    likeCount: number;
  };
}

// Cache data interfaces
interface CharactersCacheData {
  characters: CharacterItem[];
  firstTimestamp: string | null;
  lastTimestamp: string | null;
  timestamp: number;
}

// ORIFOLI Card component - exactly matching CharacterCreateModal design
interface OrifoliCardProps {
  character: CharacterItem;
  onClick?: () => void;
}

// ORIFOLI Adopt Card component - for characters for sale
interface OrifoliAdoptCardProps {
  character: CharacterForSale;
  onClick?: () => void;
}

const OrifoliCard: React.FC<OrifoliCardProps> = ({ character, onClick }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const { t } = useTranslation('top');
  const cardTheme = character.cardTheme || 'dark';

  // Use thumbnailUrl for explore tab (watermarked), with fallback
  const displayImageUrl = character.thumbnailUrl || character.avatarUrl || character.headerImageUrl;

  // Get display info
  const displayName = character.ownerName || character.ownerUsername || 'Creator';
  const username = character.ownerUsername || '';

  const cardContent = (
    <div className="bg-white p-2 rounded-2xl shadow-lg hover:shadow-2xl transition-shadow">
        <div className="relative aspect-[2/3] rounded-xl overflow-hidden">
          {/* Thumbnail image (watermarked) */}
          {displayImageUrl && !imageError ? (
            <img
              src={displayImageUrl}
              alt={character.name}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
              onContextMenu={(e) => e.preventDefault()}
              draggable={false}
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center">
              <PhotoIcon className="w-12 h-12 text-gray-300" />
            </div>
          )}

          {/* Top Overlay - Logo */}
          <div className={`absolute top-0 left-0 right-0 pt-2.5 pb-8 px-4 bg-gradient-to-b ${
            cardTheme === 'dark' ? 'from-black/40 to-transparent' : 'from-white/60 to-transparent'
          }`}>
            <span className={`text-[10px] font-medium tracking-[0.2em] uppercase ${
              cardTheme === 'dark' ? 'text-white/80' : 'text-black/70'
            }`}>
              ORIFOLI
            </span>
          </div>

          {/* English Name - Top Right, Vertical (bottom to top) */}
          {character.nameEnglish && (
            <div
              className="absolute top-4 right-1 z-10"
              style={{
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
              }}
            >
              <span className={`text-[10px] font-light tracking-wider whitespace-nowrap ${
                cardTheme === 'dark' ? 'text-white/80' : 'text-black/80'
              }`}>
                {character.nameEnglish}
              </span>
            </div>
          )}

          {/* Bottom Overlay - Character Info */}
          <div className={`absolute bottom-0 left-0 right-0 pt-20 pb-3 px-4 bg-gradient-to-t ${
            cardTheme === 'dark' ? 'from-black/60 via-black/40 to-transparent' : 'from-white/90 via-white/60 to-transparent'
          }`}>
            {/* Character Name */}
            <p className={`text-xl font-semibold tracking-tight line-clamp-2 ${
              cardTheme === 'dark' ? 'text-white' : 'text-black/60'
            }`} style={{ lineHeight: '1.2' }}>
              {character.name}
            </p>

            {/* Name Reading (よみがな) */}
            {character.nameReading && (
              <p className={`text-[9px] mt-0.5 ${
                cardTheme === 'dark' ? 'text-white/80' : 'text-black/80'
              }`}>
                {character.nameReading}
              </p>
            )}

            {/* Bio */}
            {character.bio && (
              <p className={`text-[10px] mt-1 line-clamp-2 leading-relaxed ${
                cardTheme === 'dark' ? 'text-white/70' : 'text-gray-600'
              }`}>
                {character.bio}
              </p>
            )}

            {/* Tags Row */}
            {character.tags && character.tags.length > 0 && (
              <div className="flex gap-1 mt-0.5 min-h-[14px]">
                {character.tags.slice(0, 3).map(tag => {
                  // Try to translate the tag from any category
                  const translatedTag =
                    t(`tags.genre.${tag}`, { defaultValue: '' }) ||
                    t(`tags.personality.${tag}`, { defaultValue: '' }) ||
                    t(`tags.worldview.${tag}`, { defaultValue: tag });
                  return (
                    <span
                      key={tag}
                      className={`text-[9px] ${cardTheme === 'dark' ? 'text-white/50' : 'text-gray-500'}`}
                    >
                      #{translatedTag}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Divider */}
            <div className={`border-t my-3 ${cardTheme === 'dark' ? 'border-white/20' : 'border-gray-300'}`} />

            {/* Creator Info */}
            <div className="flex items-center gap-2">
              <div className={`w-5 h-5 rounded-full overflow-hidden flex-shrink-0 relative ${
                cardTheme === 'dark' ? 'bg-white/20' : 'bg-gray-200'
              }`}>
                <div className={`absolute inset-0 bg-gradient-to-br ${
                  cardTheme === 'dark' ? 'from-white/30 to-white/10' : 'from-gray-300 to-gray-200'
                }`} />
                {/* 画像読み込み完了後にアバターをレンダリング */}
                {imageLoaded && character.ownerAvatar && (
                  <img
                    src={character.ownerAvatar}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                )}
              </div>
              <p className={`text-[9px] tracking-wide truncate ${
                cardTheme === 'dark' ? 'text-white/50' : 'text-gray-500'
              }`}>
                <span className={cardTheme === 'dark' ? 'text-white/30' : 'text-gray-400'}>CREATOR/</span>
                {displayName}
                {username && <span className={cardTheme === 'dark' ? 'text-white/30' : 'text-gray-400'}> @{username}</span>}
              </p>
            </div>
          </div>
        </div>
      </div>
  );

  // Use div with onClick if onClick is provided, otherwise use Link
  if (onClick) {
    return (
      <div onClick={onClick} className="block group cursor-pointer">
        {cardContent}
      </div>
    );
  }

  return (
    <Link to={`/character/${character.id}`} className="block group">
      {cardContent}
    </Link>
  );
};

// おむかえ用カードコンポーネント - 価格バッジ付き（サムネイル表示）
const OrifoliAdoptCard: React.FC<OrifoliAdoptCardProps> = ({ character, onClick }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const { t } = useTranslation('top');
  const cardTheme = character.cardTheme || 'dark';

  // Use thumbnailUrl for adopt cards (watermarked)
  const displayImageUrl = character.thumbnailUrl;
  const displayName = character.ownerName || character.ownerUsername || 'Creator';
  const username = character.ownerUsername || '';

  const cardContent = (
    <div className="bg-white p-2 rounded-2xl shadow-lg hover:shadow-2xl transition-shadow">
      <div className="relative aspect-[2/3] rounded-xl overflow-hidden">
        {/* Thumbnail image (watermarked) */}
        {displayImageUrl && !imageError ? (
          <img
            src={displayImageUrl}
            alt={character.name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            onContextMenu={(e) => e.preventDefault()}
            draggable={false}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center">
            <PhotoIcon className="w-12 h-12 text-gray-300" />
          </div>
        )}

        {/* Price Badge - Top Right */}
        <div className="absolute top-3 right-3 px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded-full shadow-lg z-10">
          {formatPrice(character.salePrice)}
        </div>

        {/* Top Overlay - Logo */}
        <div className={`absolute top-0 left-0 right-0 pt-2.5 pb-8 px-4 bg-gradient-to-b ${
          cardTheme === 'dark' ? 'from-black/40 to-transparent' : 'from-white/60 to-transparent'
        }`}>
          <span className={`text-[10px] font-medium tracking-[0.2em] uppercase ${
            cardTheme === 'dark' ? 'text-white/80' : 'text-black/70'
          }`}>
            ORIFOLI
          </span>
        </div>

        {/* English Name - Top Right, Vertical (below price badge) */}
        {character.nameEnglish && (
          <div
            className="absolute top-12 right-1"
            style={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
            }}
          >
            <span className={`text-[10px] font-light tracking-wider whitespace-nowrap ${
              cardTheme === 'dark' ? 'text-white/80' : 'text-black/80'
            }`}>
              {character.nameEnglish}
            </span>
          </div>
        )}

        {/* Bottom Overlay - Character Info */}
        <div className={`absolute bottom-0 left-0 right-0 pt-20 pb-3 px-4 bg-gradient-to-t ${
          cardTheme === 'dark' ? 'from-black/60 via-black/40 to-transparent' : 'from-white/90 via-white/60 to-transparent'
        }`}>
          {/* Character Name */}
          <p className={`text-xl font-semibold tracking-tight line-clamp-2 ${
            cardTheme === 'dark' ? 'text-white' : 'text-black/60'
          }`} style={{ lineHeight: '1.2' }}>
            {character.name}
          </p>

          {/* Name Reading (よみがな) */}
          {character.nameReading && (
            <p className={`text-[9px] mt-0.5 ${
              cardTheme === 'dark' ? 'text-white/80' : 'text-black/80'
            }`}>
              {character.nameReading}
            </p>
          )}

          {/* Bio */}
          {character.bio && (
            <p className={`text-[10px] mt-1 line-clamp-2 leading-relaxed ${
              cardTheme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              {character.bio}
            </p>
          )}

          {/* Tags Row */}
          {character.tags && character.tags.length > 0 && (
            <div className="flex gap-1 mt-0.5 min-h-[14px]">
              {character.tags.slice(0, 3).map(tag => {
                const translatedTag =
                  t(`tags.genre.${tag}`, { defaultValue: '' }) ||
                  t(`tags.personality.${tag}`, { defaultValue: '' }) ||
                  t(`tags.worldview.${tag}`, { defaultValue: tag });
                return (
                  <span
                    key={tag}
                    className={`text-[9px] ${cardTheme === 'dark' ? 'text-white/70' : 'text-gray-500'}`}
                  >
                    #{translatedTag}
                  </span>
                );
              })}
            </div>
          )}

          {/* Divider */}
          <div className={`border-t my-2 ${cardTheme === 'dark' ? 'border-white/20' : 'border-gray-300'}`} />

          {/* Creator Info */}
          <div className="flex items-center gap-2">
            <div className={`w-5 h-5 rounded-full overflow-hidden flex-shrink-0 relative ${
              cardTheme === 'dark' ? 'bg-white/20' : 'bg-gray-200'
            }`}>
              <div className={`absolute inset-0 bg-gradient-to-br ${
                cardTheme === 'dark' ? 'from-white/30 to-white/10' : 'from-gray-300 to-gray-200'
              }`} />
              {/* 画像読み込み完了後にアバターをレンダリング */}
              {imageLoaded && character.ownerAvatar && (
                <img
                  src={character.ownerAvatar}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity"
                  onLoad={(e) => { e.currentTarget.style.opacity = '1'; }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              )}
            </div>
            <p className={`text-[9px] tracking-wide truncate ${
              cardTheme === 'dark' ? 'text-white/50' : 'text-gray-500'
            }`}>
              <span className={cardTheme === 'dark' ? 'text-white/30' : 'text-gray-400'}>CREATOR/</span>
              {displayName}
              {username && <span className={cardTheme === 'dark' ? 'text-white/30' : 'text-gray-400'}> @{username}</span>}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  if (onClick) {
    return (
      <div onClick={onClick} className="block group cursor-pointer">
        {cardContent}
      </div>
    );
  }

  return (
    <Link to={`/character/${character.id}`} className="block group">
      {cardContent}
    </Link>
  );
};

interface TopPageProps {
  initialCategory?: CategoryFilter;
}

const TopPage: React.FC<TopPageProps> = ({ initialCategory }) => {
  const { user } = useAuth();
  const { t } = useTranslation('top');
  const { isDarkMode } = useThemeStore();
  const navigate = useNavigate();

  // Category filter state (initialized from localStorage)
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>(() => {
    // URL指定があればそちらを優先
    if (initialCategory) return initialCategory;
    try {
      const cached = localStorage.getItem(CATEGORY_CACHE_KEY);
      if (cached === 'original' || cached === 'vtuber') {
        return cached;
      }
    } catch {
      // ignore
    }
    return 'original';
  });
  const currentCategoryRef = useRef<CategoryFilter>(categoryFilter);

  // Characters state
  const [characters, setCharacters] = useState<CharacterItem[]>([]);
  const [isLoadingCharacters, setIsLoadingCharacters] = useState(true);
  const [hasMoreCharacters, setHasMoreCharacters] = useState(true);
  const charactersFirstTimestamp = useRef<string | null>(null);
  const charactersLastTimestamp = useRef<string | null>(null);
  const charactersFetchingRef = useRef(false);
  const charactersInitializedRef = useRef(false);

  // Followed characters state
  const [followedCharacters, setFollowedCharacters] = useState<CharacterItem[]>([]);
  const [isLoadingFollowed, setIsLoadingFollowed] = useState(false);
  const followedInitializedRef = useRef(false);

  // Characters for sale (おむかえ) state
  const [charactersForSale, setCharactersForSale] = useState<CharacterForSale[]>([]);
  const [isLoadingForSale, setIsLoadingForSale] = useState(true);
  const forSaleInitializedRef = useRef(false);

  // おむかえセクション用フィルタ
  const [adoptGenreFilter, setAdoptGenreFilter] = useState<string | null>(null);
  const [adoptWorldviewFilter, setAdoptWorldviewFilter] = useState<string | null>(null);

  // おむかえ用ジャンルオプションの順序
  const [orderedAdoptGenreOptions, setOrderedAdoptGenreOptions] = useState<Array<{ value: string; label: string }>>(() => {
    const userId = user?.uid || 'anon';
    const cacheKey = `orifoli_adopt_genre_order_${userId}`;
    try {
      const saved = localStorage.getItem(cacheKey);
      if (saved) {
        const savedOrder: string[] = JSON.parse(saved);
        const options = [...CHARACTER_TAGS.genre.options];
        const reordered = savedOrder
          .map(value => options.find(opt => opt.value === value))
          .filter((opt): opt is typeof options[0] => opt !== undefined);
        const remaining = options.filter(opt => !savedOrder.includes(opt.value));
        return [...reordered, ...remaining];
      }
    } catch {
      // Ignore parse errors
    }
    return [...CHARACTER_TAGS.genre.options];
  });

  // UI state
  const [activeTab, setActiveTab] = useState<'explore' | 'follow'>('explore');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Filter state
  const [selectedGenreFilter, setSelectedGenreFilter] = useState<string | null>(null);
  const [selectedPersonalityFilter, setSelectedPersonalityFilter] = useState<string | null>(null);
  const [selectedWorldviewFilter, setSelectedWorldviewFilter] = useState<string | null>(null);

  // Ordered genre options (for reordering on click)
  const [orderedGenreOptions, setOrderedGenreOptions] = useState<Array<{ value: string; label: string }>>(() => {
    // Load saved order from localStorage
    const userId = user?.uid || 'anon';
    const cacheKey = `orifoli_genre_order_${userId}`;
    try {
      const saved = localStorage.getItem(cacheKey);
      if (saved) {
        const savedOrder: string[] = JSON.parse(saved);
        // Reorder based on saved order
        const options = [...CHARACTER_TAGS.genre.options];
        const reordered = savedOrder
          .map(value => options.find(opt => opt.value === value))
          .filter((opt): opt is typeof options[0] => opt !== undefined);
        // Add any new options that weren't in saved order
        const remaining = options.filter(opt => !savedOrder.includes(opt.value));
        return [...reordered, ...remaining];
      }
    } catch {
      // Ignore parse errors
    }
    return [...CHARACTER_TAGS.genre.options];
  });

  // Update ordered options when user changes
  useEffect(() => {
    const userId = user?.uid || 'anon';
    // 探索用
    const cacheKey = `orifoli_genre_order_${userId}`;
    try {
      const saved = localStorage.getItem(cacheKey);
      if (saved) {
        const savedOrder: string[] = JSON.parse(saved);
        const options = [...CHARACTER_TAGS.genre.options];
        const reordered = savedOrder
          .map(value => options.find(opt => opt.value === value))
          .filter((opt): opt is typeof options[0] => opt !== undefined);
        const remaining = options.filter(opt => !savedOrder.includes(opt.value));
        setOrderedGenreOptions([...reordered, ...remaining]);
      } else {
        setOrderedGenreOptions([...CHARACTER_TAGS.genre.options]);
      }
    } catch {
      setOrderedGenreOptions([...CHARACTER_TAGS.genre.options]);
    }
    // おむかえ用
    const adoptCacheKey = `orifoli_adopt_genre_order_${userId}`;
    try {
      const saved = localStorage.getItem(adoptCacheKey);
      if (saved) {
        const savedOrder: string[] = JSON.parse(saved);
        const options = [...CHARACTER_TAGS.genre.options];
        const reordered = savedOrder
          .map(value => options.find(opt => opt.value === value))
          .filter((opt): opt is typeof options[0] => opt !== undefined);
        const remaining = options.filter(opt => !savedOrder.includes(opt.value));
        setOrderedAdoptGenreOptions([...reordered, ...remaining]);
      } else {
        setOrderedAdoptGenreOptions([...CHARACTER_TAGS.genre.options]);
      }
    } catch {
      setOrderedAdoptGenreOptions([...CHARACTER_TAGS.genre.options]);
    }
  }, [user?.uid]);

  // Handle genre button click - move to front and save order
  const handleGenreClick = useCallback((clickedValue: string) => {
    // Toggle selection
    setSelectedGenreFilter(prev => prev === clickedValue ? null : clickedValue);

    // Move clicked option to front
    setOrderedGenreOptions(prev => {
      const clickedIndex = prev.findIndex(opt => opt.value === clickedValue);
      if (clickedIndex <= 0) return prev; // Already at front or not found

      const clicked = prev[clickedIndex];
      const newOrder = [clicked, ...prev.filter((_, i) => i !== clickedIndex)];

      // Save to localStorage
      const userId = user?.uid || 'anon';
      const cacheKey = `orifoli_genre_order_${userId}`;
      try {
        localStorage.setItem(cacheKey, JSON.stringify(newOrder.map(opt => opt.value)));
      } catch {
        // Ignore storage errors
      }

      return newOrder;
    });
  }, [user?.uid]);

  // Handle adopt genre button click - move to front and save order
  const handleAdoptGenreClick = useCallback((clickedValue: string) => {
    // Toggle selection
    setAdoptGenreFilter(prev => prev === clickedValue ? null : clickedValue);

    // Move clicked option to front
    setOrderedAdoptGenreOptions(prev => {
      const clickedIndex = prev.findIndex(opt => opt.value === clickedValue);
      if (clickedIndex <= 0) return prev; // Already at front or not found

      const clicked = prev[clickedIndex];
      const newOrder = [clicked, ...prev.filter((_, i) => i !== clickedIndex)];

      // Save to localStorage
      const userId = user?.uid || 'anon';
      const cacheKey = `orifoli_adopt_genre_order_${userId}`;
      try {
        localStorage.setItem(cacheKey, JSON.stringify(newOrder.map(opt => opt.value)));
      } catch {
        // Ignore storage errors
      }

      return newOrder;
    });
  }, [user?.uid]);

  // Filtered characters
  const filteredCharacters = useMemo(() => {
    if (!selectedGenreFilter && !selectedPersonalityFilter && !selectedWorldviewFilter) {
      return characters;
    }
    return characters.filter(character => {
      const tags = character.tags || [];
      if (selectedGenreFilter && !tags.includes(selectedGenreFilter)) return false;
      if (selectedPersonalityFilter && !tags.includes(selectedPersonalityFilter)) return false;
      if (selectedWorldviewFilter && !tags.includes(selectedWorldviewFilter)) return false;
      return true;
    });
  }, [characters, selectedGenreFilter, selectedPersonalityFilter, selectedWorldviewFilter]);

  // Filtered characters for sale (おむかえ)
  const filteredCharactersForSale = useMemo(() => {
    if (!adoptGenreFilter && !adoptWorldviewFilter) {
      return charactersForSale;
    }
    return charactersForSale.filter(character => {
      const tags = character.tags || [];
      if (adoptGenreFilter && !tags.includes(adoptGenreFilter)) return false;
      if (adoptWorldviewFilter && !tags.includes(adoptWorldviewFilter)) return false;
      return true;
    });
  }, [charactersForSale, adoptGenreFilter, adoptWorldviewFilter]);

  // おむかえフィルタがアクティブかチェック
  const hasAdoptActiveFilter = adoptGenreFilter || adoptWorldviewFilter;

  // おむかえフィルタをクリア
  const clearAdoptFilters = useCallback(() => {
    setAdoptGenreFilter(null);
    setAdoptWorldviewFilter(null);
  }, []);

  // Check if any filter is active
  const hasActiveFilter = selectedGenreFilter || selectedPersonalityFilter || selectedWorldviewFilter;

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSelectedGenreFilter(null);
    setSelectedPersonalityFilter(null);
    setSelectedWorldviewFilter(null);
  }, []);

  // Modal state
  const [selectedCharacter, setSelectedCharacter] = useState<CharacterItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  // Handle character click - open modal
  const handleCharacterClick = useCallback((character: CharacterItem) => {
    setSelectedCharacter(character);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedCharacter(null);
  }, []);

  // Handle follow tab click - requires login
  const handleFollowTabClick = useCallback(() => {
    if (!user) {
      setIsLoginModalOpen(true);
      return;
    }
    setActiveTab('follow');
  }, [user]);

  // Ref for intersection observer target
  const loadingRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  // Cache functions - include user ID and category for per-user caching
  const getCacheKey = useCallback((category?: CategoryFilter) => {
    const userId = user?.uid || 'anon';
    const cat = category || currentCategoryRef.current;
    return `oc_feed_${activeTab}_${cat}_${userId}`;
  }, [user?.uid, activeTab]);

  const saveToCache = useCallback((data: CharacterItem[]) => {
    if (!data || data.length === 0) return;
    try {
      const cacheData: CharactersCacheData = {
        characters: data,
        firstTimestamp: charactersFirstTimestamp.current,
        lastTimestamp: charactersLastTimestamp.current,
        timestamp: Date.now()
      };
      localStorage.setItem(getCacheKey(), JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving cache:', error);
    }
  }, [getCacheKey]);

  const loadFromCache = useCallback((): CharactersCacheData | null => {
    try {
      const cached = localStorage.getItem(getCacheKey());
      if (cached) {
        const data: CharactersCacheData = JSON.parse(cached);
        if (Date.now() - data.timestamp < CACHE_EXPIRY_TIME && data.characters?.length > 0) {
          return data;
        }
        localStorage.removeItem(getCacheKey());
      }
    } catch (error) {
      console.error('Error loading cache:', error);
      localStorage.removeItem(getCacheKey());
    }
    return null;
  }, [getCacheKey]);

  // Fetch characters
  const fetchCharacters = useCallback(async (isInitial: boolean = false, category?: CategoryFilter): Promise<boolean> => {
    if (charactersFetchingRef.current || (!isInitial && !hasMoreCharacters)) {
      return false;
    }

    try {
      charactersFetchingRef.current = true;
      setIsLoadingCharacters(true);

      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (!isInitial && charactersLastTimestamp.current) {
        params.set('before', charactersLastTimestamp.current);
      }
      // Add category filter
      const cat = category || currentCategoryRef.current;
      params.set('category', cat);

      const response = await fetch(`${config.apiBaseUrl}/api/orifoli/public/characters?${params}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch characters: ${response.status}`);
      }

      const data = await response.json();
      const newCharacters = data.characters || [];

      if (newCharacters.length === 0 || !data.hasMore) {
        setHasMoreCharacters(false);
      }

      const existingIds = isInitial ? new Set<string>() : new Set(characters.map(c => c.id));
      const uniqueNew = newCharacters.filter((c: CharacterItem) => !existingIds.has(c.id));

      if (uniqueNew.length > 0 || isInitial) {
        setCharacters(prev => {
          const updated = isInitial ? uniqueNew : [...prev, ...uniqueNew];

          if (newCharacters.length > 0) {
            const first = newCharacters[0];
            const last = newCharacters[newCharacters.length - 1];
            if (!charactersFirstTimestamp.current || isInitial) {
              charactersFirstTimestamp.current = first?.createdAt || null;
            }
            charactersLastTimestamp.current = last?.createdAt || null;
          }

          saveToCache(updated);
          return updated;
        });
      }

      return true;
    } catch (error) {
      // API未デプロイ時やネットワークエラー時は空の状態を表示
      console.warn('Error fetching characters (API may not be deployed):', error);
      setHasMoreCharacters(false);
      return false;
    } finally {
      setIsLoadingCharacters(false);
      charactersFetchingRef.current = false;
    }
  }, [hasMoreCharacters, characters, saveToCache]);

  // Fetch newer characters (items created after cache)
  const fetchNewerCharacters = useCallback(async (category?: CategoryFilter) => {
    if (!charactersFirstTimestamp.current || charactersFetchingRef.current) {
      return;
    }

    try {
      charactersFetchingRef.current = true;

      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        after: charactersFirstTimestamp.current
      });
      // Add category filter
      const cat = category || currentCategoryRef.current;
      params.set('category', cat);

      const response = await fetch(`${config.apiBaseUrl}/api/orifoli/public/characters?${params}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch newer characters: ${response.status}`);
      }

      const data = await response.json();
      const newCharacters = data.characters || [];

      if (newCharacters.length > 0) {
        setCharacters(prev => {
          // Filter out duplicates using previous state
          const existingIds = new Set(prev.map(c => c.id));
          const uniqueNew = newCharacters.filter((c: CharacterItem) => !existingIds.has(c.id));

          if (uniqueNew.length === 0) return prev;

          // Prepend new items to the beginning
          const updated = [...uniqueNew, ...prev];

          // Update firstTimestamp to the newest item
          const first = uniqueNew[0];
          if (first?.createdAt) {
            charactersFirstTimestamp.current = first.createdAt;
          }

          // Save to cache (need to call outside setState for proper closure)
          setTimeout(() => {
            try {
              const cacheData: CharactersCacheData = {
                characters: updated,
                firstTimestamp: charactersFirstTimestamp.current,
                lastTimestamp: charactersLastTimestamp.current,
                timestamp: Date.now()
              };
              localStorage.setItem(getCacheKey(), JSON.stringify(cacheData));
            } catch (e) {
              console.error('Error saving cache:', e);
            }
          }, 0);

          return updated;
        });
      }
    } catch (error) {
      console.warn('Error fetching newer characters:', error);
    } finally {
      charactersFetchingRef.current = false;
    }
  }, [getCacheKey]);

  // Fetch followed characters
  const fetchFollowedCharacters = useCallback(async () => {
    if (!user) return;
    setIsLoadingFollowed(true);
    try {
      const characters = await getFollowedCharacters(user.uid);
      // Convert Character to CharacterItem format
      const items: CharacterItem[] = characters.map(c => ({
        id: c.id,
        name: c.name,
        bio: c.bio,
        noise1Url: c.noise1Url,
        noise2Url: c.noise2Url,
        rid: c.rid,
        imageWidth: c.imageWidth,
        imageHeight: c.imageHeight,
        thumbnailUrl: c.thumbnailUrl,
        fallbackNoise1Url: c.fallbackNoise1Url,
        fallbackNoise2Url: c.fallbackNoise2Url,
        avatarUrl: c.avatarUrl,
        headerImageUrl: c.headerImageUrl,
        tags: c.tags,
        cardTheme: c.cardTheme,
        ownerId: c.ownerId,
        ownerName: c.ownerName,
        ownerUsername: c.ownerUsername,
        ownerAvatar: c.ownerAvatar,
        createdAt: c.createdAt?.toDate?.()?.toISOString(),
        stats: c.stats,
      }));
      setFollowedCharacters(items);
    } catch (error) {
      console.error('Error fetching followed characters:', error);
    } finally {
      setIsLoadingFollowed(false);
    }
  }, [user]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    if (isRefreshing || isLoadingCharacters || isLoadingFollowed) return;
    setIsRefreshing(true);

    try {
      if (activeTab === 'explore') {
        localStorage.removeItem(getCacheKey());
        setCharacters([]);
        charactersFirstTimestamp.current = null;
        charactersLastTimestamp.current = null;
        setHasMoreCharacters(true);
        charactersFetchingRef.current = false;
        await fetchCharacters(true);
      } else {
        await fetchFollowedCharacters();
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [activeTab, isRefreshing, isLoadingCharacters, isLoadingFollowed, fetchCharacters, fetchFollowedCharacters, getCacheKey]);

  // Initialize characters
  useEffect(() => {
    if (charactersInitializedRef.current) return;
    charactersInitializedRef.current = true;

    const cachedData = loadFromCache();
    if (cachedData && cachedData.characters.length > 0) {
      setCharacters(cachedData.characters);
      setIsLoadingCharacters(false);
      charactersFirstTimestamp.current = cachedData.firstTimestamp;
      charactersLastTimestamp.current = cachedData.lastTimestamp;

      // Fetch newer items in background after loading cache
      fetchNewerCharacters();
    } else {
      fetchCharacters(true);
    }
  }, [loadFromCache, fetchCharacters, fetchNewerCharacters, categoryFilter]);

  // Initialize followed characters when follow tab selected
  useEffect(() => {
    if (activeTab !== 'follow' || followedInitializedRef.current || !user) return;
    followedInitializedRef.current = true;
    fetchFollowedCharacters();
  }, [activeTab, user, fetchFollowedCharacters]);

  // Reset followed characters when user changes
  useEffect(() => {
    followedInitializedRef.current = false;
    setFollowedCharacters([]);
  }, [user?.uid]);

  // Fetch characters for sale (おむかえ)
  useEffect(() => {
    if (forSaleInitializedRef.current) return;
    forSaleInitializedRef.current = true;

    const fetchForSale = async () => {
      try {
        setIsLoadingForSale(true);
        const response = await listCharactersForSale({ limit: 8, category: currentCategoryRef.current });
        setCharactersForSale(response.characters);
      } catch (error) {
        console.warn('Error fetching characters for sale:', error);
      } finally {
        setIsLoadingForSale(false);
      }
    };

    fetchForSale();
  }, [categoryFilter]);

  // Native Intersection Observer for infinite scroll
  useEffect(() => {
    const target = loadingRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setInView(entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: '0px 0px 400px 0px',
      }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  // Trigger fetch when in view
  useEffect(() => {
    if (inView && !isLoadingCharacters && hasMoreCharacters && activeTab === 'explore') {
      fetchCharacters(false);
    }
  }, [inView, isLoadingCharacters, hasMoreCharacters, activeTab, fetchCharacters]);

  return (
    <div
      className="text-gray-900 dark:text-gray-100 select-none"
      onContextMenu={(e) => e.preventDefault()}
      onCopy={(e) => e.preventDefault()}
    >
      {/* Create Banner */}
      <div className="bg-[#f8f7f8] dark:bg-[#2a2a3e] px-6 py-6 my-2">
        <div className="container mx-auto flex items-center justify-between">
          <p className="text-black dark:text-gray-100 text-sm lg:text-lg font-bold">
            {t('banner.message')}
          </p>
          <button
            onClick={() => user ? setIsCreateModalOpen(true) : setIsLoginModalOpen(true)}
            className="ml-4 px-4 py-2 bg-[#ccff00] text-[#000000] text-xs font-bold rounded-full hover:bg-[#c0f000] transition-colors whitespace-nowrap flex-shrink-0"
          >
            {t('banner.createButton')}
          </button>
        </div>
      </div>

      {/* Content Type Header (Original characters / Vtuber) - 選択中のタイプが先頭、スムーズアニメーション */}
      <div className="pt-4 px-4 md:px-8 max-w-8xl mx-auto">
        <LayoutGroup>
          <div className="flex items-center gap-8 mb-4">
            {(categoryFilter === 'original'
              ? (['original', 'vtuber'] as const)
              : (['vtuber', 'original'] as const)
            ).map((type) => (
              <motion.button
                key={type}
                layout
                transition={{ type: 'tween', duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                onClick={() => {
                  if (categoryFilter !== type) {
                    setCategoryFilter(type);
                    currentCategoryRef.current = type;
                    // Update URL: /vtuber for vtuber, / for original
                    navigate(type === 'vtuber' ? '/vtuber' : '/', { replace: true });
                    // Save preference
                    try {
                      localStorage.setItem(CATEGORY_CACHE_KEY, type);
                    } catch {
                      // ignore
                    }
                    // Reset characters when category changes
                    setCharacters([]);
                    setHasMoreCharacters(true);
                    charactersFirstTimestamp.current = null;
                    charactersLastTimestamp.current = null;
                    charactersFetchingRef.current = false;
                    // Keep initialized=true to prevent useEffect from racing with fetchCharacters
                    charactersInitializedRef.current = true;
                    // Reset for sale characters too
                    setCharactersForSale([]);
                    forSaleInitializedRef.current = false;
                    // Fetch new data
                    fetchCharacters(true, type);
                  }
                }}
                className={`text-lg md:text-2xl font-bold tracking-wide transition-colors ${
                  categoryFilter === type
                    ? 'text-gray-900 dark:text-gray-100'
                    : 'text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400'
                }`}
              >
                {type === 'original' ? t('header.original', 'Original characters') : t('header.vtuber', 'Vtuber')}
                {categoryFilter === type ? (
                  <motion.div
                    key={`underline-active-${categoryFilter}`}
                    layout
                    className="h-1 mt-1 rounded-full bg-[#CCFF00]"
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: [0, 1, 0, 1, 0, 1],
                    }}
                    transition={{
                      duration: 2,
                      delay: 0.6,
                      times: [0, 0.15, 0.35, 0.55, 0.75, 1],
                      ease: 'linear',
                    }}
                  />
                ) : (
                  <div className="h-1 mt-1 rounded-full bg-transparent" />
                )}
              </motion.button>
            ))}
          </div>
        </LayoutGroup>
      </div>

      {/* Tabs */}
      <div className="pb-3 px-4 md:px-8 max-w-8xl mx-auto">
        <div className="flex items-center border-b border-gray-200 dark:border-gray-700">
          <div className="flex gap-1 overflow-x-auto flex-1">
            <button
              onClick={() => setActiveTab('explore')}
              className={`px-4 py-2 md:px-6 md:py-3 text-base md:text-lg font-bold transition-colors relative whitespace-nowrap flex-shrink-0 ${
                activeTab === 'explore'
                  ? 'text-gray-900 dark:text-gray-100'
                  : 'text-[#b0b2b4] dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400'
              }`}
            >
              {t('tabs.explore')}
              {activeTab === 'explore' && (
                <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#CCFF00] dark:bg-[#CCFF00]" />
              )}
            </button>
            <button
              onClick={handleFollowTabClick}
              className={`px-4 py-2 md:px-6 md:py-3 text-base md:text-lg font-bold transition-colors relative whitespace-nowrap flex-shrink-0 ${
                activeTab === 'follow'
                  ? 'text-gray-900 dark:text-gray-100'
                  : 'text-[#b0b2b4] dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400'
              }`}
            >
              {t('tabs.follow')}
              {activeTab === 'follow' && (
                <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#CCFF00] dark:bg-[#CCFF00]" />
              )}
            </button>
          </div>

          {/* Refresh button */}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing || isLoadingCharacters || isLoadingFollowed}
            className={`ml-2 p-2 rounded-full transition-all duration-200 flex-shrink-0 ${
              isRefreshing ? 'opacity-70' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
            title={t('common:buttons.refresh', { defaultValue: 'Refresh' })}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-5 w-5 text-gray-400 ${isRefreshing ? 'animate-spin' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Filter Section (only for explore tab) */}
      {activeTab === 'explore' && (
        <div className="px-4 md:px-8 max-w-8xl mx-auto mb-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {/* ジャンル */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className="text-[12px] text-gray-400 dark:text-gray-400 font-bold mx-2">{t('filter.genre')}</span>
              <AnimatePresence mode="popLayout">
                {orderedGenreOptions.map(option => (
                  <motion.button
                    key={option.value}
                    layout
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    onClick={() => handleGenreClick(option.value)}
                    className={`px-2 py-0.5 rounded-full text-[11px] transition-colors whitespace-nowrap ${
                      selectedGenreFilter === option.value
                        ? 'bg-[#CCFF00] text-black font-medium'
                        : 'bg-white/80 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t(`tags.genre.${option.value}`, { defaultValue: option.label })}
                  </motion.button>
                ))}
              </AnimatePresence>
            </div>

            {/* 区切り */}
            <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 flex-shrink-0 mx-2" />

            {/* 性格 */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className="text-[12px] text-gray-400 dark:text-gray-400 font-bold mr-2">{t('filter.personality')}</span>
              {CHARACTER_TAGS.personality.options.map(option => (
                <button
                  key={option.value}
                  onClick={() => setSelectedPersonalityFilter(prev => prev === option.value ? null : option.value)}
                  className={`px-2 py-0.5 rounded-full text-[11px] transition-all whitespace-nowrap ${
                    selectedPersonalityFilter === option.value
                      ? 'bg-[#CCFF00] text-black font-medium'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {t(`tags.personality.${option.value}`, { defaultValue: option.label })}
                </button>
              ))}
            </div>

            {/* 区切り */}
            <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 flex-shrink-0 mx-2" />

            {/* 世界観 */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className="text-[12px] text-gray-400 dark:text-gray-400 font-bold mr-2">{t('filter.worldview')}</span>
              {CHARACTER_TAGS.worldview.options.map(option => (
                <button
                  key={option.value}
                  onClick={() => setSelectedWorldviewFilter(prev => prev === option.value ? null : option.value)}
                  className={`px-2 py-0.5 rounded-full text-[11px] transition-all whitespace-nowrap ${
                    selectedWorldviewFilter === option.value
                      ? 'bg-[#CCFF00] text-black font-medium'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {t(`tags.worldview.${option.value}`, { defaultValue: option.label })}
                </button>
              ))}
            </div>

            {/* Clear Filters */}
            {hasActiveFilter && (
              <>
                <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 flex-shrink-0" />
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-0.5 text-[11px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors flex-shrink-0"
                >
                  <XMarkIcon className="w-3 h-3" />
                  {t('filter.clear')}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <section className="pb-12 px-4 md:px-8 max-w-8xl mx-auto">
        {activeTab === 'explore' ? (
          <>
            <div className="min-h-[200px]">
              {filteredCharacters.length > 0 ? (
                <div className="grid grid-cols-1 min-[600px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
                  {filteredCharacters.map((character) => (
                    <OrifoliCard
                      key={character.id}
                      character={character}
                      onClick={() => handleCharacterClick(character)}
                    />
                  ))}
                </div>
              ) : isLoadingCharacters ? (
                <div className="flex justify-center items-center py-20">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 dark:border-gray-400"></div>
                </div>
              ) : hasActiveFilter ? (
                <div className="text-center py-20">
                  <PhotoIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">{t('empty.noMatch')}</p>
                  <button
                    onClick={clearFilters}
                    className="px-4 py-1 bg-gray-600/50 rounded-full mt-4 text-sm text-[#ffffff] hover:bg-gray-600/80"
                  >
                    {t('empty.clearFilter')}
                  </button>
                </div>
              ) : (
                <div className="text-center py-20">
                  <PhotoIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">{t('empty.noCharacters')}</p>
                </div>
              )}
            </div>

            {/* Intersection observer target + loading indicator */}
            {hasMoreCharacters && (
              <div ref={loadingRef} className="flex justify-center items-center py-6">
                {isLoadingCharacters && (
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400 dark:border-gray-500"></div>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {followedCharacters.length > 0 ? (
              <div className="grid grid-cols-1 min-[600px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
                {followedCharacters.map((character) => (
                  <OrifoliCard
                    key={character.id}
                    character={character}
                    onClick={() => handleCharacterClick(character)}
                  />
                ))}
              </div>
            ) : isLoadingFollowed ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 dark:border-gray-400"></div>
              </div>
            ) : (
              <div className="text-center py-20">
                <PhotoIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">{t('empty.noFollowed')}</p>
              </div>
            )}
          </>
        )}
      </section>

      {/* おむかえセクション */}
      {(charactersForSale.length > 0 || isLoadingForSale) && (
        <section className="py-8 px-4 md:px-8 max-w-8xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <HomeIcon className="w-6 h-6 text-pink-500" />
              <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">
                {t('adopt.title')}
              </h2>
            </div>
            <Link
              to="/adopt"
              className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              {t('adopt.seeMore')}
              <ChevronRightIcon className="w-4 h-4" />
            </Link>
          </div>

          {/* おむかえフィルタ */}
          <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-3 scrollbar-hide">
            {/* ジャンル */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className="text-[11px] text-gray-400 dark:text-gray-500 font-medium mr-1">{t('filter.genre')}</span>
              <AnimatePresence mode="popLayout">
                {orderedAdoptGenreOptions.map(option => (
                  <motion.button
                    key={option.value}
                    layout
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    onClick={() => handleAdoptGenreClick(option.value)}
                    className={`px-2 py-0.5 rounded-full text-[10px] transition-colors whitespace-nowrap ${
                      adoptGenreFilter === option.value
                        ? 'bg-pink-500 text-white font-medium'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t(`tags.genre.${option.value}`, { defaultValue: option.label })}
                  </motion.button>
                ))}
              </AnimatePresence>
            </div>

            {/* 区切り */}
            <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 flex-shrink-0" />

            {/* 世界観 */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className="text-[11px] text-gray-400 dark:text-gray-500 font-medium mr-1">{t('filter.worldview')}</span>
              {CHARACTER_TAGS.worldview.options.map(option => (
                <button
                  key={option.value}
                  onClick={() => setAdoptWorldviewFilter(prev => prev === option.value ? null : option.value)}
                  className={`px-2 py-0.5 rounded-full text-[10px] transition-colors whitespace-nowrap ${
                    adoptWorldviewFilter === option.value
                      ? 'bg-pink-500 text-white font-medium'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {t(`tags.worldview.${option.value}`, { defaultValue: option.label })}
                </button>
              ))}
            </div>

            {/* クリアボタン */}
            {hasAdoptActiveFilter && (
              <>
                <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 flex-shrink-0" />
                <button
                  onClick={clearAdoptFilters}
                  className="flex items-center gap-0.5 text-[10px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors flex-shrink-0"
                >
                  <XMarkIcon className="w-3 h-3" />
                  {t('filter.clear')}
                </button>
              </>
            )}
          </div>

          {isLoadingForSale ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 dark:border-gray-400"></div>
            </div>
          ) : filteredCharactersForSale.length === 0 ? (
            <div className="text-center py-8">
              <HomeIcon className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {hasAdoptActiveFilter ? t('adopt.noMatch') : t('adopt.noCharacters')}
              </p>
              {hasAdoptActiveFilter && (
                <button
                  onClick={clearAdoptFilters}
                  className="mt-2 text-xs text-pink-500 hover:underline"
                >
                  {t('empty.clearFilter')}
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 min-[600px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
              {filteredCharactersForSale.slice(0, 4).map((char) => (
                <OrifoliAdoptCard
                  key={char.id}
                  character={char}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* ORIFOLIとは セクション */}
      <section className="pt-10 px-4 md:px-4 max-w-8xl mx-auto">
        <div className="rounded-2xl px-6 md:px-6">
          <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('about.title', 'ORIFOLIとは')}
          </h2>
          <p className="text-gray-700 dark:text-gray-300 text-sm md:text-base leading-relaxed mb-4">
            {t('about.description', 'ORIFOLI（オリフォリ）は、オリジナルキャラクターの魅力を引き立てるプロフィール x ポートフォリオサービスです。あなたの創作キャラクターやVTuberのプロフィールを、美しいカード形式で作成・共有できます。')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-white/60 dark:bg-gray-700/50 rounded-xl p-4">
              <h3 className="font-bold text-gray-900 dark:text-gray-100 text-sm mb-2">
                {t('about.feature1.title', 'プロフィール作成')}
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {t('about.feature1.description', '名前、設定、性格などキャラクターの魅力を余すことなく記録できます')}
              </p>
            </div>
            <div className="bg-white/60 dark:bg-gray-700/50 rounded-xl p-4">
              <h3 className="font-bold text-gray-900 dark:text-gray-100 text-sm mb-2">
                {t('about.feature2.title', 'ファンアート投稿')}
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {t('about.feature2.description', 'キャラクターへのファンアートを投稿・共有。クリエイターとファンを繋ぎます')}
              </p>
            </div>
            <div className="bg-white/60 dark:bg-gray-700/50 rounded-xl p-4">
              <h3 className="font-bold text-gray-900 dark:text-gray-100 text-sm mb-2">
                {t('about.feature3.title', '設定資料管理')}
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {t('about.feature3.description', '三面図や衣装ラフなど、キャラクターの設定資料を一元管理できます')}
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* 作品掲載ポリシーセクション */}
      <section className="py-8 px-4 md:px-4 max-w-8xl mx-auto">
        <div className="px-6 md:px-6">
          <h2 className="text-base md:text-lg font-bold text-gray-800 dark:text-gray-100 mb-3">
            {t('policy.title')}
          </h2>
          <p className="text-gray-800 dark:text-gray-300 text-xs md:text-xs leading-relaxed">
            {t('policy.description')}
          </p>
          <Link to="https://figryne.com/ai-policy" className="inline-block text-gray-900 dark:text-gray-200 text-[9px] md:text-[10px] mt-1 hover:text-gray-600 dark:hover:text-gray-400 underline transition-colors">
            {t('policy.aiPolicyLink')}
          </Link>
        </div>
      </section>


      {/* FIGRYNE KDVR テクノロジーセクション */}
      <section className="pt-4 pb-8 px-4 md:px-4 max-w-8xl mx-auto">
        <div className="rounded-2xl px-6 md:px-6">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6 md:gap-8">
            {/* Left: Logo */}
            <div className="flex-shrink-0">
              <img
                src={isDarkMode ? kdvrLogoWhite : kdvrLogo}
                alt="FIGRYNE KDVR"
                className="h-16 object-contain pt-1"
              />
            </div>
            {/* Right: Text */}
            <div className="flex-1 md:text-left">
              <p className="text-gray-600 dark:text-gray-200 text-xs md:text-sm leading-relaxed">
                {t('kdvr.description', 'ORIFOLIでは独自の画像伝送表示技術「FIGRYNE KDVR（Key-Dependent Visual Reconstruction）」を採用。画像を複数のレイヤーに分解して配信し、クライアント上でリアルタイムに再構成することで、高いセキュリティと高速な表示を両立しています。')}
              </p>
              <p className="text-gray-400 text-[10px] mt-1">
                {t('kdvr.note', '※ お預かりした画像は分解して保存されており、元の形ではサーバーにも存在しません')}
              </p>
            </div>
          </div>
        </div>
      </section>



      {/* Card Viewer Modal */}
      <CardViewerModal
        character={selectedCharacter}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />

      {/* Character Create Wizard */}
      <CharacterCreateWizard
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        defaultCategory="original"
      />

      {/* Login Modal */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onSuccess={() => setActiveTab('follow')}
      />
    </div>
  );
};

export default TopPage;
